import React, { useEffect, useState } from 'react';
import useDragTranslate from '../hooks/useDragTranslate';
import { getRandomWindowPosition } from '../utils/windowPositioning';
import TEXT from '../constants/text';
/* projects folder window component */

const ProjectsWindow = ({ onClose, zIndex, onFocus, onProjectClick }) => {
  const { elRef, onPointerDown, onPointerMove, onPointerUp } = useDragTranslate(onFocus);
  const [selectedIndex, setSelectedIndex] = useState(3);
  const [isMobile, setIsMobile] = useState(false);
  /* drag functionality and selection state */
  
  // Generate random position for this window
  const [windowPosition] = useState(() => getRandomWindowPosition(700, 500));
  
  const handleFocus = () => {
    onFocus && onFocus();
    /* bring window to front when clicked */
  };

  const handleClose = () => {
    // 1) add .closing
    elRef.current?.classList.add('closing');
    /* start the close animation */

    // 2) after animation end, call onClose to unmount
    const done = () => {
      elRef.current?.removeEventListener('animationend', done);
      onClose();
    };
    elRef.current?.addEventListener('animationend', done, { once: true });
    /* wait for animation to finish then actually close */
  };

  // Detect mobile devices and narrow desktop windows
  useEffect(() => {
    const checkMobile = () => {
      const isMobileWidth = window.innerWidth <= 770;
      setIsMobile(isMobileWidth);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // listen on the document so we can move outside the window bounds
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    /* set up drag event listeners */
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      /* clean up listeners */
    };
  }, [onPointerMove, onPointerUp]);

  const projects = [
    { 
      name: TEXT.PROJECTS.ROUNDEX.NAME, 
      date: TEXT.PROJECTS.ROUNDEX.DATE, 
      type: TEXT.PROJECTS.ROUNDEX.TYPE,
      detailData: {
        title: TEXT.PROJECTS.ROUNDEX.TITLE,
        subtitle: TEXT.PROJECTS.ROUNDEX.SUBTITLE,
        image: TEXT.PROJECTS.ROUNDEX.IMAGE,
        sections: TEXT.PROJECTS.ROUNDEX.SECTIONS
      }
    },
    { 
      name: TEXT.PROJECTS.SPELLCASTER.NAME, 
      date: TEXT.PROJECTS.SPELLCASTER.DATE, 
      type: TEXT.PROJECTS.SPELLCASTER.TYPE,
      detailData: {
        title: TEXT.PROJECTS.SPELLCASTER.TITLE,
        subtitle: TEXT.PROJECTS.SPELLCASTER.SUBTITLE,
        image: TEXT.PROJECTS.SPELLCASTER.IMAGE,
        sections: TEXT.PROJECTS.SPELLCASTER.SECTIONS
      }
    },
    { 
      name: TEXT.PROJECTS.FINORIA.NAME, 
      date: TEXT.PROJECTS.FINORIA.DATE, 
      type: TEXT.PROJECTS.FINORIA.TYPE,
      detailData: {
        title: TEXT.PROJECTS.FINORIA.TITLE,
        subtitle: TEXT.PROJECTS.FINORIA.SUBTITLE,
        image: TEXT.PROJECTS.FINORIA.IMAGE,
        sections: TEXT.PROJECTS.FINORIA.SECTIONS
      }
    },
    { 
      name: TEXT.PROJECTS.ONE_WAY_OUT.NAME, 
      date: TEXT.PROJECTS.ONE_WAY_OUT.DATE, 
      type: TEXT.PROJECTS.ONE_WAY_OUT.TYPE,
      detailData: {
        title: TEXT.PROJECTS.ONE_WAY_OUT.TITLE,
        subtitle: TEXT.PROJECTS.ONE_WAY_OUT.SUBTITLE,
        image: TEXT.PROJECTS.ONE_WAY_OUT.IMAGE,
        sections: TEXT.PROJECTS.ONE_WAY_OUT.SECTIONS
      }
    }
  ];
  /* projects list with new data - newest to oldest */

  return (
    <div
      ref={elRef}
      className="window"
      data-window="projects"
      style={isMobile ? {
        display: 'block',
        width: '100vw',
        height: 'calc(100vh - 35px)',
        zIndex: zIndex,
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '35px',
        transform: 'translate(0, 0)',
        // Mobile: full screen layout
      } : {
        display: 'block',
        width: '700px',
        height: '500px',
        zIndex: zIndex,
        position: 'fixed',
        '--tx': `${windowPosition.tx}px`,
        '--ty': `${windowPosition.ty}px`,
        // Desktop: draggable window
      }}
      onClick={handleFocus}
    >
      <div className="title-bar draggable" onPointerDown={onPointerDown}>
        <div className="title-bar-text">Projects - Windows Explorer</div>
        <div className="title-bar-controls">
          <button aria-label="Close" onClick={handleClose}></button>
        </div>
      </div>
      {/* window title bar with close button */}
      
      <div className="window-body">
        {isMobile ? (
          <div className="project-cards-container">
            {projects.map((project, index) => (
              <div 
                key={index} 
                className={`project-card ${selectedIndex === index ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedIndex(index);
                  onProjectClick && onProjectClick(project.detailData);
                }}
              >
                <div className="card-header">
                  <div className="card-name">
                    <img src="/assets/filepixel.png" alt="File" className="card-icon" />
                    {project.name}
                  </div>
                </div>
                <div className="card-date">{project.date}</div>
                <div className="card-content">
                  <div className="card-type">{project.type}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="explorer-list">
            <div className="list-header">
              <div className="header-name">Name</div>
              <div className="header-date">Dates modified</div>
              <div className="header-type">Type</div>
            </div>
            {/* explorer list header */}
            
            {projects.map((project, index) => (
              <div 
                key={index} 
                className={`list-item ${selectedIndex === index ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedIndex(index);
                  onProjectClick && onProjectClick(project.detailData);
                }}
              >
                <div className="item-name">
                  <img src="/assets/filepixel.png" alt="File" className="item-icon" />
                  {project.name}
                </div>
                <div className="item-date">{project.date}</div>
                <div className="item-type">{project.type}</div>
              </div>
            ))}
            {/* explorer list items */}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsWindow;
